import React from 'react';

import { useInfiniteQuery } from 'react-query';
import { DotBricksLoading } from 'src/common/components';

import { fetchAccountCohortHistory, fetchRecipientCohortHistory } from '../api';
import { COHORT_HISTORY_QUERY_KEY } from '../constants';
import { CohortHistoryKind } from '../types';
import {
  describeCohortHistoryChange,
  formatCohortHistoryDate,
  getCohortHistoryActorLabel,
} from '../utils';

type CohortHistoryProps = {
  kind: CohortHistoryKind;
  cohortId: number;
};

const FETCH_HISTORY = {
  recipient: fetchRecipientCohortHistory,
  account: fetchAccountCohortHistory,
};

const CohortHistory: React.FC<CohortHistoryProps> = ({ kind, cohortId }) => {
  const { data, isLoading, isError, fetchMore, canFetchMore, isFetchingMore } = useInfiniteQuery(
    [COHORT_HISTORY_QUERY_KEY, kind, cohortId],
    (_key: string, historyKind: CohortHistoryKind, id: number, page: unknown) =>
      FETCH_HISTORY[historyKind](id, typeof page === 'number' ? page : 1),
    { getFetchMore: (lastPage, allPages) => (lastPage.data.next ? allPages.length + 1 : false) }
  );

  if (isLoading) return <DotBricksLoading />;
  if (isError) {
    return (
      <p className="py-12 text-sm text-center text-red-600">No se pudo cargar el historial.</p>
    );
  }

  const entries = data?.flatMap((page) => page.data.results) ?? [];
  if (entries.length === 0) {
    return (
      <p className="py-12 text-sm text-center text-gray-400">Todavía no hay cambios registrados.</p>
    );
  }

  return (
    <div>
      <ul className="rounded-2xl overflow-hidden bg-white border border-gray-200 shadow-sm">
        {entries.map((entry) => (
          <li
            key={entry.id}
            className="last:border-0 flex items-start gap-4 px-5 py-3 text-sm border-b border-gray-100"
          >
            <span className="flex-shrink-0 w-40 text-xs text-gray-500">
              {formatCohortHistoryDate(entry.created_at)}
            </span>
            <span className="flex-1 min-w-0 text-gray-700 break-words">
              <span className="font-medium text-gray-900">{getCohortHistoryActorLabel(entry)}</span>{' '}
              {describeCohortHistoryChange(entry)}
            </span>
            {entry.source === 'csv' && (
              <span className="flex-shrink-0 px-2 py-0.5 text-xs font-semibold text-gray-600 bg-gray-100 rounded-full">
                CSV
              </span>
            )}
          </li>
        ))}
      </ul>
      {canFetchMore && (
        <div className="flex justify-center mt-3">
          <button
            type="button"
            className="btn btn-ghost disabled:opacity-50 px-4 py-1.5 text-xs border border-gray-200 rounded-full"
            disabled={!!isFetchingMore}
            onClick={() => fetchMore()}
          >
            {isFetchingMore ? 'Cargando…' : 'Cargar más'}
          </button>
        </div>
      )}
    </div>
  );
};

export default CohortHistory;
